import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';
import { useAuth } from '../lib/auth/AuthContext';
import { useFocusOnMount } from '../lib/hooks/useFocusOnMount';
import { ApiError } from '../lib/api';
import {
  getEventState,
  getEventWeeks,
  postWeeklyConsent,
  type EventWeek,
} from '../lib/api/event';
import { FREE_PLAY_WEEK } from '../lib/eventState';
import {
  bucketSeconds,
  elapsedWeeklyConsentSeconds,
  endWeeklyConsentClock,
  startWeeklyConsentClock,
} from '../lib/signupTiming';
import { trackEvent } from '../lib/plausible';
import { Button } from '../components/Button';
import { Toggle } from '../components/form/Toggle';
import { FormError } from '../components/form/FormError';
import { BackLink } from '../components/register/BackLink';
import { HeadingUnderscore, RegisterLayout } from '../components/register/RegisterLayout';

/**
 * /weekly-rules — returning-user consent gate.
 *
 * Two toggles per Figma:
 *   - Age (UI-only): re-confirmed each week as a defensive contest
 *     practice. We don't write a new ageConfirmedAt — the original is still
 *     authoritative.
 *   - Contest + Privacy: combined per Figma. We record the weekly rules
 *     consent via POST /event/rules/consent.
 *
 * On success → /play (which will then issue the game token).
 */
export default function WeeklyRulesPage() {
  const navigate = useNavigate();
  const auth = useAuth();

  const [eventWeeks, setEventWeeks] = useState<EventWeek[] | null>(null);
  const [currentWeek, setCurrentWeek] = useState<number | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);

  const [age, setAge] = useState(false);
  const [rules, setRules] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // True between a successful submit and the React-Router nav to /play.
  // Keeps the toggles + button disabled through the transition.
  const [transitioning, setTransitioning] = useState(false);
  const busy = pending || transitioning;
  const headingRef = useFocusOnMount<HTMLHeadingElement>();

  useEffect(() => {
    applyPageMeta(PAGE_META.weeklyRules);
  }, []);

  useEffect(() => {
    startWeeklyConsentClock();
  }, []);

  // Only ACTIVE users have business on this page. Anonymous → /register.
  // PENDING → /register (finish profile + initial consent). Anything else
  // (SUSPENDED or a future status) → /play, which has the canonical gate.
  useEffect(() => {
    if (auth.status === 'loading') return;
    if (!auth.isAuthenticated || auth.user?.status === 'PENDING') {
      navigate('/register', { replace: true });
      return;
    }
    if (auth.user?.status !== 'ACTIVE') {
      navigate('/play', { replace: true });
    }
  }, [auth.status, auth.isAuthenticated, auth.user, navigate]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getEventState(), getEventWeeks()])
      .then(([state, weeks]) => {
        if (cancelled) return;
        setCurrentWeek(state.currentWeek);
        setEventWeeks(weeks.weeks);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[GH] /weekly-rules boot fetch failed', err);
        setBootError(
          "We couldn't load the current week's rules. Please refresh and try again.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentRules = useMemo(() => {
    if (!eventWeeks || currentWeek === null) return null;
    return eventWeeks.find((w) => w.weekNumber === currentWeek)?.rules ?? null;
  }, [eventWeeks, currentWeek]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!age || !rules || !currentRules || currentWeek === null || busy) return;
    setSubmitError(null);
    setPending(true);
    try {
      await postWeeklyConsent({
        weekNumber: currentWeek,
        rulesVersion: currentRules.version,
        source: 'play-gate',
        clientTs: new Date().toISOString(),
      });
      const seconds = elapsedWeeklyConsentSeconds();
      trackEvent('Weekly Consent', {
        weekNumber: currentWeek,
        durationBucket: bucketSeconds(seconds),
      });
      endWeeklyConsentClock();
      setTransitioning(true);
      navigate('/play', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setSubmitError(
          "This week's rules were just updated. Please refresh and re-read before agreeing.",
        );
      } else if (err instanceof ApiError && err.status >= 500) {
        setSubmitError('Something went wrong on our end. Please try again.');
      } else {
        setSubmitError('Network error. Check your connection and try again.');
      }
    } finally {
      setPending(false);
    }
  }, [age, rules, currentRules, currentWeek, navigate, busy]);

  if (auth.status === 'loading') {
    return (
      <RegisterLayout>
        <p className="text-center font-['Arial',sans-serif] text-white">Checking your session…</p>
      </RegisterLayout>
    );
  }

  if (bootError) {
    return (
      <RegisterLayout>
        <FormError>{bootError}</FormError>
      </RegisterLayout>
    );
  }

  if (!currentRules || currentWeek === null) {
    return (
      <RegisterLayout>
        <p className="text-center font-['Arial',sans-serif] text-white">Loading this week's rules…</p>
      </RegisterLayout>
    );
  }

  const allRequired = age && rules;
  // Post-contest Free Play (GHS5-85): the "weekly rules" for week 6+ are the
  // Free Play Terms and Conditions, so drop the "Week N" framing.
  const isFreePlay = currentWeek >= FREE_PLAY_WEEK;

  return (
    <RegisterLayout
      heading={
        <>
          register
          <HeadingUnderscore />
        </>
      }
    >
      <BackLink onClick={() => navigate('/')} disabled={busy} label="Cancel" />
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-center font-['Orbitron',sans-serif] text-[clamp(16px,4.8vw,22px)] uppercase tracking-[0.1em] text-white focus:outline-none"
      >
        {isFreePlay ? 'Free Play Terms and Conditions' : `Rules — Week ${currentWeek}`}
      </h2>

      <form
        className="flex flex-col items-stretch gap-[18px]"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        noValidate
      >
        <Toggle
          checked={age}
          onChange={setAge}
          required
          disabled={busy}
          label={
            <>
              I am 18 years or older.{' '}
              <span className="text-white/75">
                (For AL, DE, and NE residents you must be 19 years or older and for MS residents
                you must be 21 years or older.)
              </span>
            </>
          }
        />

        <Toggle
          checked={rules}
          onChange={setRules}
          required
          disabled={busy}
          label={
            <>
              I have reviewed and agree to the{' '}
              <a
                href="/rules"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-[#00ffb2]"
              >
                {isFreePlay ? 'Free Play Terms and Conditions' : 'Official Gamerhood Contest'}
              </a>{' '}
              and{' '}
              <a
                href="https://www.statefarm.com/customer-care/privacy-security/privacy"
                target="_blank"
                rel="noreferrer noopener"
                className="underline hover:text-[#00ffb2]"
              >
                Privacy Policy
              </a>
              .
            </>
          }
        />

        <FormError>{submitError}</FormError>

        <div className="flex flex-col items-center gap-[16px]">
          <Button
            variant="dark"
            type="submit"
            disabled={!allRequired || busy}
            className="min-w-[200px]"
          >
            {pending ? 'Saving…' : 'Next'}
          </Button>
          <div className="min-h-[16px]">
            {!allRequired && (
              <p className="font-['Arial',sans-serif] text-[12px] text-white/70">
                Please confirm both to continue.
              </p>
            )}
          </div>
        </div>
      </form>
    </RegisterLayout>
  );
}
